/*
Copyright 2026 Dexnet / iON.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

/*
 * iON (audit 9): a camera or microphone that did not turn on is shown as a
 * toast; the call goes on without it. Failures of both devices close
 * together make one toast, not two.
 */

import { BehaviorSubject, fromEvent, map, NEVER, switchMap } from "rxjs";
import { MediaDeviceFailure, RoomEvent } from "livekit-client";

import { type Behavior } from "../state/Behavior";
import { type ObservableScope } from "../state/ObservableScope";
import { type Connection } from "../state/CallViewModel/remoteMembers/Connection";
import {
  ACTION_TEXTS,
  type IonMediaDevice,
  type IonMediaFailure,
  MEDIA_TOAST_TEXTS,
  PERMISSION_HINTS,
  pick,
} from "./callErrorTexts";

export interface MediaFailureToast {
  device: IonMediaDevice;
  failure: IonMediaFailure;
}

function deviceOf(kind: MediaDeviceKind | undefined): IonMediaDevice {
  if (kind === "videoinput") return "camera";
  if (kind === "audioinput") return "microphone";
  // enableCameraAndMicrophone fails for both at once without a kind
  return "both";
}

/**
 * The toast to show for failures of the local camera and microphone, and a
 * way to dismiss it.
 */
export function createMediaFailures(
  scope: ObservableScope,
  localConnection$: Behavior<Connection | null>,
): {
  mediaFailure$: Behavior<MediaFailureToast | null>;
  dismissMediaFailure: () => void;
  /** Tries to turn the failed device(s) on again; a new failure shows again. */
  retryMediaFailure: () => void;
} {
  const toast$ = new BehaviorSubject<MediaFailureToast | null>(null);
  localConnection$
    .pipe(
      switchMap((connection) =>
        connection
          ? fromEvent(
              connection.livekitRoom,
              RoomEvent.MediaDevicesError,
              (error: Error, kind?: MediaDeviceKind) => ({ error, kind }),
            )
          : NEVER,
      ),
      map(({ error, kind }) => ({
        device: deviceOf(kind),
        failure: (MediaDeviceFailure.getFailure(error) ??
          "Other") as IonMediaFailure,
      })),
      scope.bind(),
    )
    .subscribe((next) => {
      const shown = toast$.value;
      // Camera and microphone failing together: one toast for both, with
      // the reason of the first
      if (shown && shown.device !== next.device)
        toast$.next({ device: "both", failure: shown.failure });
      else toast$.next(next);
    });
  const retry = (): void => {
    const toast = toast$.value;
    const participant = localConnection$.value?.livekitRoom.localParticipant;
    toast$.next(null);
    if (!toast || !participant) return;
    const turnOn =
      toast.device === "camera"
        ? participant.setCameraEnabled(true)
        : toast.device === "microphone"
          ? participant.setMicrophoneEnabled(true)
          : participant.enableCameraAndMicrophone();
    // A failure comes again as MediaDevicesError, and so as a toast
    turnOn.catch(() => {});
  };
  return {
    mediaFailure$: toast$,
    dismissMediaFailure: () => toast$.next(null),
    retryMediaFailure: retry,
  };
}

/** Whether trying again can help: not without permission or a device. */
export function canRetry(toast: MediaFailureToast): boolean {
  return toast.failure === "DeviceInUse" || toast.failure === "Other";
}

/** The toast's action: "Try again" where it can help. */
export function mediaFailureAction(
  toast: MediaFailureToast,
  language: string | undefined,
  retry: () => void,
): { label: string; onClick: () => void } | undefined {
  return canRetry(toast)
    ? { label: pick(ACTION_TEXTS.retry, language), onClick: retry }
    : undefined;
}

/** The toast's line; "no access" says where to allow it. */
export function mediaFailureText(
  toast: MediaFailureToast,
  language: string | undefined,
  inBrowser: boolean,
): string {
  const text = pick(MEDIA_TOAST_TEXTS[toast.failure][toast.device], language);
  if (toast.failure !== "PermissionDenied") return text;
  const hint = inBrowser ? PERMISSION_HINTS.browser : PERMISSION_HINTS.phone;
  return `${text}. ${pick(hint, language)}`;
}
